import { SourcingOpenings } from 'api-clients/monolith';
import { format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

import VectorIcon from '@/assets/icons/vector.svg';
import { Tag } from '@/components/ui/tag';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { getChannelName } from '@/utils/channel-names';

export function ActionTakenNotifier({
  metrics,
}: {
  metrics: SourcingOpenings['at_risk_metrics'];
}) {
  const intl = useIntl();

  if (!metrics) return null;

  return (
    <Tooltip
      content={
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Typography variant="caption" className="text-white">
              <FormattedMessage
                defaultMessage="Action taken"
                description="Tooltip badge: recruiter already addressed this at-risk recommendation"
              />
            </Typography>
            <span className="size-1 rounded-full bg-white" />
            <Typography variant="caption" className="text-white">
              {format(metrics.action_taken_on, 'MMMM d')}
            </Typography>
          </div>

          <Typography variant="small2" className="text-white">
            {metrics.recommendation_type === 'general' ? (
              <FormattedMessage
                defaultMessage="{count, plural, one {Posted to # channel} other {Posted to # channels}}"
                description="Past action summary after posting to boards; plural by channel count"
                values={{ count: metrics.sourcing_channels.length }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Budget increased on {channel}"
                description="Past action summary for single-board budget change; {channel} is board name"
                values={{
                  channel: getChannelName(metrics.sourcing_channels[0]),
                }}
              />
            )}
          </Typography>

          <Typography variant="small1" className="text-white">
            {metrics.recommendation_type === 'general' ? (
              <FormattedMessage
                defaultMessage="Added {channels}. Watching for applicant lift in the next 3-5 days."
                description="Follow-up sentence listing boards added via general recommendation"
                values={{
                  channels: intl.formatList(
                    metrics.sourcing_channels.map(slug => getChannelName(slug)),
                    {
                      type: 'conjunction',
                    },
                  ),
                }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Monitoring for applicant lift over the next 5-7 days. Changes typically take 8-10 days to reflect in hiring pace."
                description="Tooltip follow-up after budget adjustment: timeline for observing impact"
              />
            )}
          </Typography>
        </div>
      }
    >
      <span>
        <Tag icon={VectorIcon} className="bg-fx-amber-50 !w-6 !px-1" />
      </span>
    </Tooltip>
  );
}
